import { lazy } from 'react';
import type { Configuration } from 'containers/Schema/schema.types';
import { getNotificationRowId } from 'containers/Notifications/advancedNotificationTypes';
import { decimalToTimeString } from 'containers/Shifts/components/ShiftProperties';

const NestAwareNewDialog = lazy(
  () => import('containers/Tags/dialogs/NestAwareNewDialog')
);
const NewConfigurationDialog = lazy(
  () => import('containers/Configuration/dialogs/New')
);
const NewJobDialog = lazy(() => import('containers/Jobs/dialogs/New'));
const NewNotificationEntryDialog = lazy(
  () => import('containers/Notifications/dialogs/NewNotificationEntryDialog')
);
const NewPartDialog = lazy(() => import('containers/Parts/dialogs/New'));
const NewShiftDialog = lazy(() => import('containers/Shifts/dialogs/New'));
const UpdateConfigurationDialog = lazy(
  () => import('containers/Configuration/dialogs/Update')
);
const UpdateNotificationEntryDialog = lazy(
  () => import('containers/Notifications/dialogs/UpdateNotificationEntryDialog')
);
const UpdateJobDialog = lazy(() => import('containers/Jobs/dialogs/Update'));
const UpdateMachineRecsDialog = lazy(
  () => import('containers/MachineRecs/dialogs/Update')
);
const UpdatePartDialog = lazy(() => import('containers/Parts/dialogs/Update'));
const UpdateShiftDialog = lazy(() => import('containers/Shifts/dialogs/Update'));
const ImportChecksheetDialog = lazy(
  () => import('containers/ChecksheetData/dialogs/Import')
);
const ViewReportDialog = lazy(() => import('containers/Reports/dialogs/View'));
const NewResourceDialog = lazy(() => import('containers/Resources/dialogs/New'));
const UpdateResourceDialog = lazy(
  () => import('containers/Resources/dialogs/Update')
);
const EditEmployeeDialog = lazy(
  () => import('containers/Employees/dialogues/EditEmployeeDialog')
);
const NewEmployeeDialog = lazy(
  () => import('containers/Employees/dialogues/NewEmployeeDialog')
);
const DeleteMachineReasonCategoriesDialog = lazy(
  () => import('containers/MachineReasonCategories/dialogs/Delete')
);

export type BaseModuleName =
  | 'Alerts'
  | 'Employees'
  | 'Checksheet Data'
  | 'Configuration'
  | 'Indirects'
  | 'Jobs'
  | 'Labor Records'
  | 'Notifications'
  | 'Machine Reasons'
  | 'Machine Reason Categories'
  | 'Machine Records'
  | 'Parts'
  | 'Reasons'
  | 'Reports'
  | 'Request Types'
  | 'Resources'
  | 'Scope Filters'
  | 'Shifts'
  | 'Tags';

export const baseModuleConfigs: Record<BaseModuleName, Configuration> = {
  Alerts: {
    itemType: 'alerts',
    itemLabel: 'Alert',
    listViewColumns: [
      { field: 'alertType', minWidth: 150 },
      { field: 'resourceID', minWidth: 200 },
      { field: 'partNum', minWidth: 200 },
      { field: 'creationDate', minWidth: 200, excludeFromGlobalSearch: true },
      {
        field: 'expirationDate',
        minWidth: 200,
        excludeFromGlobalSearch: true,
      },
    ],
  },
  Employees: {
    itemType: 'employees',
    itemLabel: 'Employee',
    listViewColumns: [
      { field: 'empID', minWidth: 150 },
      { field: 'firstName', minWidth: 200 },
      { field: 'lastName', minWidth: 200 },
    ],
    bulkUpdateDisabled: true,
    checkboxSelectionDisabled: true,
    defaultFilterModel: {
      items: [
        {
          field: 'active',
          operator: 'equals',
          value: 'true',
        },
      ],
    },
    customDialogs: {
      update: EditEmployeeDialog,
      new: NewEmployeeDialog,
    },
  },
  'Checksheet Data': {
    itemType: 'checkData',
    itemLabel: 'Checksheet Data',
    listViewColumns: [
      { field: 'measureDate', minWidth: 200, excludeFromGlobalSearch: true },
      { field: 'checksheetName', minWidth: 200 },
      { field: 'stepName', minWidth: 200 },
      { field: 'empID', minWidth: 150 },
      { field: 'jobNum', minWidth: 150 },
      { field: 'escape', minWidth: 200, excludeFromGlobalSearch: true },
    ],
    defaultSortModel: [
      {
        field: 'measureDate',
        sort: 'desc',
      },
    ],
    customDialogs: {
      import: ImportChecksheetDialog,
    },
  },
  Configuration: {
    itemType: 'configuration',
    itemLabel: 'Configuration Setting',
    listViewColumns: [
      { field: 'configurationCategory', minWidth: 175 },
      { field: 'configurationKey', minWidth: 250 },
      { field: 'scopeType', minWidth: 170 },
      { field: 'scopeValue', minWidth: 150 },
      { field: 'comments', minWidth: 300 },
      { field: 'active', minWidth: 200, excludeFromGlobalSearch: true },
    ],
    customDialogs: {
      new: NewConfigurationDialog,
      update: UpdateConfigurationDialog,
    },
  },
  Indirects: {
    itemType: 'indirectCodes',
    itemLabel: 'Indirect Code',
    listViewColumns: [
      { field: 'indirectCode', minWidth: 150 },
      { field: 'description', minWidth: 200 },
    ],
  },
  Jobs: {
    itemType: 'jobs',
    itemLabel: 'Job',
    listViewColumns: [
      { field: 'jobNum', minWidth: 150 },
      { field: 'partNum', minWidth: 200 },
      { field: 'partDescription', minWidth: 350 },
      { field: 'prodQty', minWidth: 200, excludeFromGlobalSearch: true },
      { field: 'status', minWidth: 200, excludeFromGlobalSearch: true },
    ],
    defaultSortModel: [
      {
        field: 'jobNum',
        sort: 'desc',
      },
    ],
    defaultFilterModel: {
      items: [
        {
          field: 'status',
          operator: 'isAnyOf',
          value: 'Open,Engineered,Firm,Released,InProcess',
        },
      ],
    },
    customDialogs: {
      new: NewJobDialog,
      update: UpdateJobDialog,
    },
  },
  'Labor Records': {
    itemType: 'laborRecs',
    itemLabel: 'Labor Record',
    listViewColumns: [
      { field: 'empID', minWidth: 150 },
      { field: 'laborType', minWidth: 200 },
      { field: 'startDate', minWidth: 200, excludeFromGlobalSearch: true },
      { field: 'endDate', minWidth: 200, excludeFromGlobalSearch: true },
      { field: 'active', minWidth: 100, excludeFromGlobalSearch: true },
    ],
    defaultFilterModel: {
      items: [
        {
          field: 'active',
          operator: 'equals',
          value: 'true',
        },
      ],
    },
  },
  'Machine Reasons': {
    itemType: 'machineReasons',
    itemLabel: 'Machine Reason',
    listViewColumns: [
      { field: 'reasonCode', minWidth: 150 },
      { field: 'reasonType', minWidth: 150 },
      { field: 'reasonCategory', minWidth: 170 },
      { field: 'description', minWidth: 200 },
    ],
    bulkUpdateDisabled: true,
  },
  'Machine Reason Categories': {
    itemType: 'machineReasonCategories',
    itemLabel: 'Machine Reason Category',
    listViewColumns: [
      { field: 'reasonCategory', minWidth: 170 },
      { field: 'description', minWidth: 250 },
    ],
    customDialogs: {
      delete: DeleteMachineReasonCategoriesDialog,
    },
  },
  'Machine Records': {
    itemType: 'machineRecs',
    itemLabel: 'Machine Record',
    listViewColumns: [
      { field: 'activityCode', minWidth: 150 },
      { field: 'resourceID', minWidth: 150 },
      { field: 'startDate', minWidth: 200, excludeFromGlobalSearch: true },
      { field: 'endDate', minWidth: 200, excludeFromGlobalSearch: true },
      { field: 'active', minWidth: 100, excludeFromGlobalSearch: true },
      {
        field: 'estActivityHrs',
        minWidth: 200,
        excludeFromGlobalSearch: true,
      },
      { field: 'activityHrs', minWidth: 200, excludeFromGlobalSearch: true },
      {
        field: 'idleReasonCode',
        minWidth: 200,
        excludeFromGlobalSearch: true,
      },
    ],
    defaultFilterModel: {
      items: [
        {
          field: 'active',
          operator: 'equals',
          value: 'true',
        },
      ],
    },
    customDialogs: {
      update: UpdateMachineRecsDialog,
    },
    addDisabled: true,
    bulkUpdateDisabled: true,
    deleteDisabled: true,
  },
  Notifications: {
    itemType: 'notifications',
    itemLabel: 'Notification',
    listViewColumns: [
      { field: 'description', minWidth: 400 },
      { field: 'notificationTypeLabel', headerName: 'Type', minWidth: 180 },
      { field: 'active', excludeFromGlobalSearch: true },
    ],
    inherentFilterModel: {
      items: [
        {
          field: 'parentNotificationID',
          operator: 'equals',
          value: 'null',
        },
      ],
    },
    getRowId: getNotificationRowId,
    exportDisabled: true,
    importDisabled: true,
    customDialogs: {
      new: NewNotificationEntryDialog,
      update: UpdateNotificationEntryDialog,
    },
  },
  Parts: {
    itemType: 'parts',
    itemLabel: 'Part',
    listViewColumns: [
      {
        field: 'partNum',
        minWidth: 250,
      },
      { field: 'description', minWidth: 400 },
    ],
    bulkUpdateDisabled: true,
    customDialogs: {
      new: NewPartDialog,
      update: UpdatePartDialog,
    },
  },
  Reasons: {
    itemType: 'reasons',
    itemLabel: 'Reason',
    listViewColumns: [
      {
        field: 'reasonCode',
        minWidth: 150,
      },
      { field: 'reasonType', minWidth: 150 },
      { field: 'description', minWidth: 200 },
    ],
  },
  Reports: {
    itemType: 'reports',
    itemLabel: 'Report',
    listViewColumns: [
      {
        field: 'report',
        minWidth: 300,
      },
      { field: 'description', minWidth: 500 },
    ],
    checkboxSelectionDisabled: true,
    getRowId: (row) => row.reportID,
    customDialogs: {
      update: ViewReportDialog,
    },
  },
  Resources: {
    itemType: 'resources',
    itemLabel: 'Resource',
    listViewColumns: [
      { field: 'resourceID', minWidth: 150 },
      { field: 'description', minWidth: 200 },
    ],
    bulkUpdateDisabled: true,
    customDialogs: {
      new: NewResourceDialog,
      update: UpdateResourceDialog,
    },
  },
  'Request Types': {
    itemType: 'requestTypes',
    itemLabel: 'Request Type',
    listViewColumns: [
      {
        field: 'requestCategory',
        minWidth: 170,
      },
      { field: 'description', minWidth: 200 },
    ],
  },
  'Scope Filters': {
    itemType: 'scopeFilters',
    itemLabel: 'Scope Filter',
    listViewColumns: [
      { field: 'itemType', minWidth: 200 },
      { field: 'field', minWidth: 200 },
      { field: 'operator', minWidth: 200 },
      { field: 'value', minWidth: 200 },
      { field: 'active', minWidth: 200, excludeFromGlobalSearch: true },
    ],
  },
  Shifts: {
    itemType: 'shifts',
    itemLabel: 'Shift',
    listViewColumns: [
      { field: 'shiftNum', minWidth: 150, headerName: 'Shift ID' },
      {
        field: 'startTime',
        minWidth: 200,
        excludeFromGlobalSearch: true,
        valueFormatter: (params: number) => decimalToTimeString(params),
      },
      {
        field: 'endTime',
        minWidth: 200,
        excludeFromGlobalSearch: true,
        valueFormatter: (params: number) => decimalToTimeString(params),
      },
      { field: 'description', minWidth: 200 },
    ],
    customDialogs: {
      new: NewShiftDialog,
      update: UpdateShiftDialog,
    },
  },
  Tags: {
    itemType: 'tagAssociations',
    itemLabel: 'Tag Associations',
    listViewColumns: [
      { field: 'epc', minWidth: 250 },
      { field: 'associationType', minWidth: 200 },
      { field: 'associationID', minWidth: 150 },
      {
        field: 'resourceID',
        minWidth: 200,
        excludeFromGlobalSearch: true,
        headerName: 'Last Location',
      },
      { field: 'active', minWidth: 200, excludeFromGlobalSearch: true },
    ],
    defaultFilterModel: {
      items: [
        {
          field: 'active',
          operator: 'equals',
          value: 'true',
        },
      ],
    },
    recordTemplate: {
      active: true,
    },
    customDialogs: {
      new: NestAwareNewDialog,
    },
  },
};
